import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useMotionValue, useSpring, useReducedMotion } from 'framer-motion';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const mediaUrl = (url) => (url && url.startsWith('/api') ? `${API_URL}${url}` : url);

const isVideoCover = (url) => url && /\.(mp4|webm|mov)(\?|$)/i.test(url);

/**
 * Portfolio-Übersicht im ppmnext-Stil:
 * – Vollhöhen-Panels, die sich beim Hover smooth ausweiten
 * – Cover-Bild/Video erscheint fließend dahinter
 * – runder Custom-Cursor mit Pfeil folgt der Maus
 * – bei mehr als 3 Portalen: horizontales Scrollen (Mausrad/Pfeile)
 */
export default function PortfolioPage() {
  const navigate = useNavigate();
  const [portals, setPortals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hovered, setHovered] = useState(null);
  const [cursorVisible, setCursorVisible] = useState(false);
  const [canScroll, setCanScroll] = useState({ left: false, right: false });
  const scrollerRef = useRef(null);
  const reduceMotion = useReducedMotion();

  // Custom-Cursor: Springs für butterweiches Nachziehen
  const cursorX = useMotionValue(-100);
  const cursorY = useMotionValue(-100);
  const springX = useSpring(cursorX, { stiffness: 300, damping: 28, mass: 0.6 });
  const springY = useSpring(cursorY, { stiffness: 300, damping: 28, mass: 0.6 });

  useEffect(() => {
    fetchPortals();
  }, []);

  const fetchPortals = async () => {
    try {
      const response = await fetch(`${API_URL}/api/portals`);
      if (response.ok) {
        setPortals(await response.json());
      }
    } catch (error) {
      console.error('Error fetching portals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMouseMove = useCallback((e) => {
    cursorX.set(e.clientX);
    cursorY.set(e.clientY);
  }, [cursorX, cursorY]);

  const updateScrollState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setCanScroll({
      left: el.scrollLeft > 4,
      right: el.scrollLeft < maxScroll - 4,
    });
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [portals, updateScrollState]);

  // Mausrad (vertikal) -> horizontales Scrollen der Panels (nur wenn scrollbar)
  const handleWheel = useCallback((e) => {
    const el = scrollerRef.current;
    if (!el) return;
    if (el.scrollWidth <= el.clientWidth + 8) return;
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    }
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [portals, handleWheel]);

  const scrollByPanel = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const panel = el.querySelector('[data-panel]');
    const width = panel ? panel.getBoundingClientRect().width : el.clientWidth / 3;
    el.scrollBy({ left: dir * width, behavior: reduceMotion ? 'auto' : 'smooth' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-white/20 border-t-yellow rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/50 text-sm uppercase tracking-wider">Lädt</p>
        </div>
      </div>
    );
  }

  const many = portals.length > 3;

  return (
    <div className="min-h-screen bg-ink flex flex-col">
      <Navigation />

      {/* Custom-Cursor (nur Desktop, nicht bei reduced motion) */}
      {!reduceMotion && (
        <motion.div
          className="fixed top-0 left-0 z-[60] pointer-events-none hidden md:flex items-center justify-center w-16 h-16 -ml-8 -mt-8 rounded-full bg-white text-ink shadow-xl"
          style={{
            x: springX,
            y: springY,
            scale: cursorVisible ? 1 : 0,
            opacity: cursorVisible ? 1 : 0,
          }}
          transition={{ scale: { duration: 0.2 } }}
        >
          <ArrowRight className="w-6 h-6" />
        </motion.div>
      )}

      <h1 className="sr-only">
        Portfolio: Foto, Video und Social Content von Someones Direction
      </h1>

      {/* Panels */}
      <div className="relative flex-1 pt-20 md:pt-24">
        <div
          ref={scrollerRef}
          onMouseMove={handleMouseMove}
          onMouseEnter={() => setCursorVisible(true)}
          onMouseLeave={() => { setCursorVisible(false); setHovered(null); }}
          className={`flex h-[calc(100vh-5rem)] md:h-[calc(100vh-6rem)] overflow-y-hidden md:cursor-none ${
            many ? 'overflow-x-auto snap-x snap-mandatory md:snap-none' : 'overflow-x-hidden flex-col sm:flex-row'
          }`}
          style={{ scrollbarWidth: 'none' }}
        >
          {portals.map((portal, index) => {
            const isHovered = hovered === portal.id;
            const someHovered = hovered !== null;
            return (
              <motion.div
                key={portal.id}
                data-panel
                initial={reduceMotion ? false : { opacity: 0, y: 60 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.12, ease: [0.22, 1, 0.36, 1] }}
                onMouseEnter={() => setHovered(portal.id)}
                onClick={() => navigate(`/portfolio/${portal.slug}`)}
                role="link"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/portfolio/${portal.slug}`); }}
                onFocus={() => setHovered(portal.id)}
                onBlur={() => setHovered(null)}
                aria-label={`Portfolio ${portal.name} öffnen`}
                className={`portal-panel group relative flex flex-col justify-between overflow-hidden px-8 py-14 md:px-10 border-r border-white/10 last:border-r-0 cursor-pointer md:cursor-none ${
                  many ? 'snap-start shrink-0 w-full sm:w-1/2 lg:w-[31%]' : 'min-h-0 flex-1'
                }`}
                style={
                  many
                    ? undefined
                    : {
                        // smooth Ausweiten: gehoverte Fläche wächst, andere ziehen sich zusammen
                        flexGrow: isHovered ? 2.2 : someHovered ? 0.75 : 1,
                        transition: reduceMotion ? 'none' : 'flex-grow 0.85s cubic-bezier(0.22, 1, 0.36, 1)',
                      }
                }
              >
                {/* Cover erscheint beim Hover */}
                {portal.cover_url && (
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      opacity: isHovered ? 1 : 0,
                      transform: isHovered ? 'scale(1)' : 'scale(1.08)',
                      transition: reduceMotion
                        ? 'none'
                        : 'opacity 0.7s ease, transform 1.4s cubic-bezier(0.22, 1, 0.36, 1)',
                    }}
                  >
                    {isVideoCover(portal.cover_url) ? (
                      <video
                        src={mediaUrl(portal.cover_url)}
                        className="w-full h-full object-cover"
                        autoPlay
                        muted
                        loop
                        playsInline
                      />
                    ) : (
                      <img
                        src={mediaUrl(portal.cover_url)}
                        alt=""
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/15 to-black/35" />
                  </div>
                )}

                {/* Nummer oben */}
                <span className="relative font-display text-white/25 text-xl">
                  {String(index + 1).padStart(2, '0')}
                </span>

                {/* Titel + Tagline mittig */}
                <div className="relative">
                  <h2
                    className="font-display leading-[0.9] uppercase break-words text-5xl md:text-6xl xl:text-7xl"
                    style={{
                      color: isHovered ? '#ffffff' : 'rgba(255,255,255,0.35)',
                      transition: 'color 0.5s ease',
                    }}
                  >
                    {portal.name}
                  </h2>
                  {portal.tagline && (
                    <p className={`mt-4 text-sm md:text-base transition-colors duration-500 max-w-xs ${isHovered ? 'text-white/90' : 'text-white/50'}`}>
                      {portal.tagline}
                    </p>
                  )}
                </div>

                {/* Kontakt unten */}
                <div className={`relative text-xs space-y-1 transition-colors duration-500 ${isHovered ? 'text-white/80' : 'text-white/40'}`}>
                  {portal.email && <p>{portal.email}</p>}
                  {portal.phone && <p>{portal.phone}</p>}
                </div>

                {/* Gelbe Unterkante beim Hover */}
                <div
                  className="absolute bottom-0 left-0 right-0 h-1 bg-yellow origin-left"
                  style={{
                    transform: isHovered ? 'scaleX(1)' : 'scaleX(0)',
                    transition: 'transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)',
                  }}
                />
              </motion.div>
            );
          })}

          {portals.length === 0 && (
            <div className="flex-1 flex items-center justify-center text-white/50">
              Noch keine Portfolios angelegt.
            </div>
          )}
        </div>

        {/* Pfeile (nur wenn scrollbar) */}
        {many && (canScroll.left || canScroll.right) && (
          <>
            <button
              onClick={() => scrollByPanel(-1)}
              disabled={!canScroll.left}
              aria-label="Nach links scrollen"
              className={`absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-yellow text-ink flex items-center justify-center shadow-lg transition-all hover:scale-110 ${
                canScroll.left ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => scrollByPanel(1)}
              disabled={!canScroll.right}
              aria-label="Nach rechts scrollen"
              className={`absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-yellow text-ink flex items-center justify-center shadow-lg transition-all hover:scale-110 ${
                canScroll.right ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
            >
              <ArrowRight className="w-5 h-5" />
            </button>
          </>
        )}
      </div>

      <Footer />
    </div>
  );
}
